import React, { useState, useContext } from "react";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    CircularProgress,
    InputAdornment,
} from "@mui/material";
import {
    ArrowBack,
    VerifiedUser,
    Construction,
    ElectricBolt,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import { AuthContext } from "../AuthContext";

const COLORS = {
    deepGreen: "#052E16",
    forest: "#14532D",
    green: "#15803D",
    emerald: "#16A34A",
    brightGreen: "#22C55E",
    freshGreen: "#4ADE80",

    gold: "#D4A017",
    brightGold: "#FACC15",

    paleGreen: "#F4FBF5",
    softGreen: "#DCFCE7",

    white: "#FFFFFF",
    text: "#102216",
    muted: "#666666",
    border: "#D8E8DC",

    error: "#B91C1C",
};

const LoginAuthentication = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setUser } = useContext(AuthContext);

    const email =
        location.state?.email ||
        localStorage.getItem("auth_email");

    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");

    const redirectUser = (user) => {
        const role = (
            user.user_type ||
            user.role ||
            ""
        ).toLowerCase();

        switch (role) {
            case "owner":
                navigate("/owner-dashboard");
                break;

            case "admin":
                navigate("/newAdminDashboard");
                break;

            case "proprietor":
                navigate("/proprietorDashboard");
                break;

            case "principal":
                navigate("/principalDashboard");
                break;

            case "teacher":
                navigate("/teacher-dashboard");
                break;

            case "student":
                navigate("/student-dashboard");
                break;

            case "parent":
                navigate("/parentsDashboard");
                break;

            case "registry":
                navigate("/registerDashboard");
                break;

            case "vpi":
                navigate("/vpiDashboard");
                break;

            case "vpa":
                navigate("/newVpaDashboard");
                break;

            case "it personel":
                navigate("/tTPersonalDashboard");
                break;

            case "business_manager":
                navigate("/businessManagerDashboard");
                break;

            case "sales_agent":
                navigate("/sales-dashboard");
                break;

            default:
                navigate("/");
        }
    };

    const verifyCode = async (e) => {
        e.preventDefault();

        if (!email) {
            setMessage(
                "Authentication email missing. Please login again."
            );
            return;
        }

        setLoading(true);
        setMessage("");

        try {
            const response = await api.post(
                "/api/users/verify-authentication-code/",
                {
                    email,
                    code,
                }
            );

            const access = response.data.access;
            const refresh = response.data.refresh;

            if (!access || !refresh) {
                throw new Error(
                    "Authentication failed. Tokens missing."
                );
            }

            localStorage.setItem("access", access);
            localStorage.setItem("refresh", refresh);

            window.dispatchEvent(
                new Event("user-logged-in")
            );

            const profile = await api.get(
                "/api/user-profile/"
            );

            const user = profile.data;

            setUser(user);

            localStorage.setItem(
                "user",
                JSON.stringify(user)
            );

            localStorage.removeItem("auth_email");

            redirectUser(user);
        } catch (error) {
            console.error(error);

            setMessage(
                error.response?.data?.error ||
                    error.response?.data?.detail ||
                    "Invalid authentication code."
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
                background: `
                    linear-gradient(
                        135deg,
                        #F4FBF5 0%,
                        #FFFFFF 45%,
                        #F7FAF8 100%
                    )
                `,
                p: 3,
            }}
        >
            {/* =====================================================
                BACKGROUND DECORATION
                Everything here stays outside the form.
            ====================================================== */}

            {/* Large green atmospheric glow */}
            <motion.div
                animate={{
                    scale: [1, 1.12, 1],
                    opacity: [0.22, 0.34, 0.22],
                }}
                transition={{
                    duration: 7,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    position: "absolute",
                    width: 480,
                    height: 480,
                    borderRadius: "50%",
                    left: -220,
                    top: -190,
                    background:
                        "radial-gradient(circle, rgba(34,197,94,.25) 0%, rgba(34,197,94,.08) 40%, transparent 72%)",
                    pointerEvents: "none",
                }}
            />

            {/* Gold atmospheric glow */}
            <motion.div
                animate={{
                    scale: [1, 1.15, 1],
                    opacity: [0.18, 0.3, 0.18],
                }}
                transition={{
                    duration: 8,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1,
                }}
                style={{
                    position: "absolute",
                    width: 520,
                    height: 520,
                    right: -250,
                    bottom: -230,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle, rgba(250,204,21,.25) 0%, rgba(212,160,23,.08) 40%, transparent 72%)",
                    pointerEvents: "none",
                }}
            />

            {/* Rotating technical ring - left */}
            <motion.div
                animate={{
                    rotate: 360,
                }}
                transition={{
                    duration: 30,
                    repeat: Infinity,
                    ease: "linear",
                }}
                style={{
                    position: "absolute",
                    left: -125,
                    bottom: 70,
                    width: 270,
                    height: 270,
                    borderRadius: "50%",
                    border: "1px dashed rgba(20,83,45,.18)",
                    pointerEvents: "none",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 16,
                        left: "50%",
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background:
                            COLORS.brightGold,
                        boxShadow:
                            "0 0 18px rgba(250,204,21,.7)",
                    }}
                />
            </motion.div>

            {/* Rotating technical ring - right */}
            <motion.div
                animate={{
                    rotate: -360,
                }}
                transition={{
                    duration: 38,
                    repeat: Infinity,
                    ease: "linear",
                }}
                style={{
                    position: "absolute",
                    right: -145,
                    top: 50,
                    width: 300,
                    height: 300,
                    borderRadius: "50%",
                    border: "1px dashed rgba(21,128,61,.16)",
                    pointerEvents: "none",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        bottom: 20,
                        left: "50%",
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        background:
                            COLORS.green,
                    }}
                />
            </motion.div>

            {/* Floating construction icon */}
            <motion.div
                animate={{
                    y: [0, -16, 0],
                    rotate: [0, 5, 0],
                }}
                transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut",
                }}
                style={{
                    position: "absolute",
                    left: "9%",
                    top: "22%",
                    width: 62,
                    height: 62,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                        "rgba(255,255,255,.72)",
                    border:
                        "1px solid rgba(20,83,45,.12)",
                    boxShadow:
                        "0 15px 35px rgba(20,83,45,.08)",
                    backdropFilter:
                        "blur(8px)",
                    pointerEvents: "none",
                }}
            >
                <Construction
                    sx={{
                        color: COLORS.forest,
                        fontSize: 29,
                    }}
                />
            </motion.div>

            {/* Floating electrical icon */}
            <motion.div
                animate={{
                    y: [0, 15, 0],
                    rotate: [0, -7, 0],
                }}
                transition={{
                    duration: 4.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 0.8,
                }}
                style={{
                    position: "absolute",
                    right: "10%",
                    bottom: "21%",
                    width: 62,
                    height: 62,
                    borderRadius: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background:
                        "rgba(255,255,255,.72)",
                    border:
                        "1px solid rgba(212,160,23,.18)",
                    boxShadow:
                        "0 15px 35px rgba(212,160,23,.10)",
                    backdropFilter:
                        "blur(8px)",
                    pointerEvents: "none",
                }}
            >
                <ElectricBolt
                    sx={{
                        color: COLORS.gold,
                        fontSize: 31,
                    }}
                />
            </motion.div>

            {/* Floating green dots */}
            {[1, 2, 3, 4, 5, 6].map((item) => (
                <motion.div
                    key={item}
                    animate={{
                        y: [0, -12, 0],
                        opacity: [0.25, 0.75, 0.25],
                    }}
                    transition={{
                        duration:
                            2.5 + item * 0.3,
                        repeat: Infinity,
                        delay: item * 0.25,
                        ease: "easeInOut",
                    }}
                    style={{
                        position: "absolute",
                        width:
                            item % 2 === 0
                                ? 7
                                : 5,
                        height:
                            item % 2 === 0
                                ? 7
                                : 5,
                        borderRadius: "50%",
                        background:
                            item % 2 === 0
                                ? COLORS.gold
                                : COLORS.green,
                        left: `${10 + item * 13}%`,
                        top:
                            item % 2 === 0
                                ? "10%"
                                : "86%",
                        pointerEvents: "none",
                    }}
                />
            ))}

            {/* Decorative diagonal lines */}
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: 230,
                    height: 170,
                    overflow: "hidden",
                    opacity: 0.28,
                    pointerEvents: "none",
                }}
            >
                {[0, 1, 2, 3, 4].map(
                    (item) => (
                        <Box
                            key={item}
                            sx={{
                                position:
                                    "absolute",
                                width: 300,
                                height: 1,
                                background:
                                    item % 2 ===
                                    0
                                        ? COLORS.green
                                        : COLORS.gold,
                                top:
                                    25 +
                                    item * 27,
                                right:
                                    -50,
                                transform:
                                    "rotate(-28deg)",
                            }}
                        />
                    )
                )}
            </Box>

            {/* Bottom-left diagonal lines */}
            <Box
                sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    width: 230,
                    height: 170,
                    overflow: "hidden",
                    opacity: 0.22,
                    pointerEvents: "none",
                }}
            >
                {[0, 1, 2, 3, 4].map(
                    (item) => (
                        <Box
                            key={item}
                            sx={{
                                position:
                                    "absolute",
                                width: 300,
                                height: 1,
                                background:
                                    item % 2 ===
                                    0
                                        ? COLORS.green
                                        : COLORS.gold,
                                bottom:
                                    25 +
                                    item * 27,
                                left:
                                    -50,
                                transform:
                                    "rotate(-28deg)",
                            }}
                        />
                    )
                )}
            </Box>

            {/* =====================================================
                ORIGINAL-SIZED FORM
            ====================================================== */}

            <motion.div
                initial={{
                    opacity: 0,
                    y: 40,
                }}
                animate={{
                    opacity: 1,
                    y: 0,
                }}
                transition={{
                    duration: 0.7,
                    ease: "easeOut",
                }}
                style={{
                    position: "relative",
                    zIndex: 5,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: "100%",
                        maxWidth: 450,
                        p: 5,
                        borderRadius: "30px",
                        background: "#FFFFFF",
                        border: `1px solid ${COLORS.border}`,
                        boxShadow:
                            "0 25px 70px rgba(5,46,22,.14)",
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    {/* Very subtle top edge only */}
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: `linear-gradient(
                                90deg,
                                ${COLORS.deepGreen},
                                ${COLORS.green},
                                ${COLORS.brightGreen},
                                ${COLORS.gold}
                            )`,
                        }}
                    />

                    <Typography
                        variant="h4"
                        fontWeight={900}
                        sx={{
                            textAlign: "center",
                            color: COLORS.forest,
                            mb: 2,
                        }}
                    >
                        Verify Login
                    </Typography>

                    <Typography
                        sx={{
                            textAlign: "center",
                            color: COLORS.muted,
                            mb: 4,
                        }}
                    >
                        Enter the authentication code
                        sent to your email.
                    </Typography>

                    <form onSubmit={verifyCode}>
                        <TextField
                            fullWidth
                            label="Authentication Code"
                            variant="filled"
                            value={code}
                            onChange={(e) =>
                                setCode(
                                    e.target.value
                                )
                            }
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <VerifiedUser
                                            sx={{
                                                color:
                                                    COLORS
                                                        .green,
                                            }}
                                        />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                mb: 3,
                                "& .MuiFilledInput-root":
                                    {
                                        borderRadius:
                                            "16px",
                                        background:
                                            "#F7FAF8",
                                        border: `1px solid ${COLORS.border}`,
                                        transition:
                                            "all .25s ease",
                                        "&:hover":
                                            {
                                                background:
                                                    "#F4FBF5",
                                            },
                                        "&.Mui-focused":
                                            {
                                                background:
                                                    "#FFFFFF",
                                                boxShadow:
                                                    "0 0 0 4px rgba(34,197,94,.08)",
                                            },
                                    },
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            disabled={loading}
                            variant="contained"
                            sx={{
                                py: 1.8,
                                borderRadius: "16px",
                                fontWeight: 900,
                                textTransform:
                                    "none",
                                background: `linear-gradient(
                                    135deg,
                                    ${COLORS.deepGreen},
                                    ${COLORS.green}
                                )`,
                                boxShadow:
                                    "0 10px 24px rgba(20,83,45,.18)",
                                "&:hover":
                                    {
                                        background: `linear-gradient(
                                            135deg,
                                            ${COLORS.forest},
                                            ${COLORS.emerald}
                                        )`,
                                        boxShadow:
                                            "0 14px 30px rgba(20,83,45,.25)",
                                    },
                            }}
                        >
                            {loading ? (
                                <Box
                                    sx={{
                                        display:
                                            "flex",
                                        alignItems:
                                            "center",
                                        justifyContent:
                                            "center",
                                        gap: 1,
                                    }}
                                >
                                    <CircularProgress
                                        size={22}
                                        sx={{
                                            color:
                                                COLORS
                                                    .brightGold,
                                        }}
                                    />

                                    Verifying...
                                </Box>
                            ) : (
                                "Verify Code"
                            )}
                        </Button>

                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() =>
                                navigate("/login")
                            }
                            startIcon={
                                <ArrowBack />
                            }
                            sx={{
                                mt: 2,
                                py: 1.5,
                                borderRadius:
                                    "16px",
                                fontWeight: 800,
                                textTransform:
                                    "none",
                                color:
                                    COLORS.forest,
                                border: `2px solid ${COLORS.gold}`,
                                "&:hover":
                                    {
                                        borderColor:
                                            COLORS
                                                .green,
                                        background:
                                            COLORS
                                                .paleGreen,
                                    },
                            }}
                        >
                            Back To Login
                        </Button>
                    </form>

                    {message && (
                        <Typography
                            sx={{
                                mt: 3,
                                textAlign: "center",
                                color:
                                    COLORS.error,
                                fontWeight: 700,
                            }}
                        >
                            {message}
                        </Typography>
                    )}
                </Paper>
            </motion.div>
        </Box>
    );
};

export default LoginAuthentication;